import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { AppLink } from "../../components/app-link.js";
import { ScreenLoader } from "../../components/loader/index.js";
import { PageState } from "../../components/page-state.js";
import { fetchIsStaff } from "../../lib/api/admin.js";
import { useMe } from "../../lib/queries/index.js";
import { queryKeys } from "../../lib/query-keys.js";
import { PATHS } from "../../lib/routes.js";
import { signInFor } from "../../lib/sign-in-path.js";
import { navigate } from "../../lib/url-filters.js";

/**
 * Who may open the operator panel (DESIGN.md §14, "Shell"). Signed out goes to
 * sign-in and comes back here after; signed in but not staff is told it is for
 * the Crux team. Nothing of the panel draws before either is known, so no card
 * fails one by one for want of a session.
 */
export function AdminGate({ children }: { readonly children: ReactNode }) {
  const me = useMe();
  const signedIn = me.data !== undefined && me.data !== null;
  const staff = useQuery({
    queryKey: queryKeys.admin("me"),
    queryFn: fetchIsStaff,
    enabled: signedIn,
  });

  // Navigation, not data fetching: the session query above is the fetch.
  useEffect(() => {
    if (me.data === null) {
      navigate(signInFor(`${window.location.pathname}${window.location.search}`), {
        replace: true,
      });
    }
  }, [me.data]);

  if (me.isError || staff.isError) {
    return (
      <PageState
        title="Could not check your access"
        actions={
          <button
            type="button"
            className="button-primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try again
          </button>
        }
      >
        Crux could not tell who is signed in just now. Nothing has changed.
      </PageState>
    );
  }
  if (!signedIn || staff.data === undefined) {
    return <ScreenLoader caption="Checking your access" />;
  }
  if (!staff.data) {
    return (
      <PageState
        title="This is for the Crux team"
        actions={
          <AppLink href={PATHS.overview} className="button-primary">
            Back to Crux
          </AppLink>
        }
      >
        The operator panel opens only for Crux staff. You are signed in as{" "}
        {me.data?.user.email}.
      </PageState>
    );
  }
  return children;
}
